// The browser Sentry setup every web app runs.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs";

import {
  getErrorNameAndMessage,
  shouldSuppressEvent,
} from "../logger/constants";
import { noPiiDataCollection } from "./dataCollection";
import { META_IN_APP_BROWSER_ERRORS } from "./ignore-errors";
import {
  deleteSensitiveHeaders,
  redactSensitiveUrl,
  redactTransactionEvent,
} from "./redact-url";
import { REPLAY_OPTIONS } from "./replay";

/** The three deployments an app reports as, in Sentry's `environment` field. */
export type SentryEnvironment = "development" | "preview" | "production";

/**
 * Which deployment this bundle is running in.
 *
 * NODE_ENV alone cannot tell a Vercel preview from production — both build with
 * NODE_ENV=production — so every preview deploy reported itself as production
 * and its errors landed in the same bucket as real ones. `NEXT_PUBLIC_VERCEL_ENV`
 * is the client-visible name, so the browser can read it.
 */
export function resolveSentryEnvironment(
  nodeEnv: string | undefined,
  vercelEnv: string | undefined,
): SentryEnvironment {
  if (nodeEnv !== "production") return "development";
  return vercelEnv === "preview" ? "preview" : "production";
}

/** How much of each signal to keep, per deployment. */
const SAMPLE_RATES: Record<
  SentryEnvironment,
  { traces: number; replaysSession: number }
> = {
  development: { traces: 1.0, replaysSession: 0.1 },
  // A preview is where a change is actually looked at, so it keeps more.
  preview: { traces: 0.5, replaysSession: 0.2 },
  production: { traces: 0.1, replaysSession: 0.1 },
};

export interface SentryClientOverrides {
  /**
   * Drop events carrying no exception (`captureMessage`, logs) in production.
   *
   * marketing-professional's verification funnel fires around ten
   * `captureMessage` calls per visit and has always dropped them in production.
   * Nothing else in the repo does, so it stays opt-in rather than becoming a
   * default that silently mutes the other three apps' message events.
   */
  dropNonErrorEventsInProduction?: boolean;
  /**
   * Leave session replay out of `init` so it starts only once the visitor has
   * agreed to analytics.
   *
   * Replay records the DOM, so on a public EU site it cannot run before the
   * answer. Opt-in rather than the default because setting it without mounting
   * `ConsentGatedReplay` ends recording for good. That is what the two portal
   * apps do until a gate lands there [EAH-2437].
   */
  gateReplayOnConsent?: boolean;
}

/**
 * Initialise the browser client. Call once, from an app's
 * `instrumentation-client.ts`. This function is the only way in, and it leaves
 * replay in `init` unless the app passes `gateReplayOnConsent`.
 */
export function configureSentryClient({
  dropNonErrorEventsInProduction = false,
  gateReplayOnConsent = false,
}: SentryClientOverrides = {}): void {
  const sentryDsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

  // Skip Sentry initialization when DSN is not configured
  if (!sentryDsn) {
    if (process.env.NODE_ENV === "development") {
      console.warn("⚠️ Sentry DSN not configured - error monitoring disabled");
    }
    return;
  }

  const environment = resolveSentryEnvironment(
    process.env.NODE_ENV,
    process.env.NEXT_PUBLIC_VERCEL_ENV,
  );
  const rates = SAMPLE_RATES[environment];

  Sentry.init({
    dsn: sentryDsn,

    // Performance Monitoring
    tracesSampleRate: rates.traces,

    // Replays for Session Replay
    replaysSessionSampleRate: rates.replaysSession,
    replaysOnErrorSampleRate: 1.0,

    // Sentry's own debug logging, on by itself for a localhost dev run with a
    // DSN set and off everywhere else: a deployed build resolves `environment`
    // to "preview" or "production", and a preview is served from a *.vercel.app
    // host, not localhost. While it is on, Sentry warns that this is not a
    // debug bundle — that warning is this line, not a problem to chase.
    debug:
      environment === "development" &&
      typeof window !== "undefined" &&
      window.location.hostname === "localhost",

    // Environment
    environment,

    // No PII by default: SDK 11's own default collects user info, cookies and
    // bodies; this keeps the SDK 10 posture. Deleting fields below does not undo it.
    dataCollection: noPiiDataCollection,
    // Sentry 11 streams spans by default and then ignores `beforeSendTransaction`,
    // the hook that scrubs emails and Stripe ids out of traced URLs; `static` keeps it live.
    traceLifecycle: "static",
    // Sentry 11 defaults this to true, which regroups every captured message; v10 posture kept.
    attachStacktrace: false,

    // Data scrubbing for sensitive information
    beforeSend(event, hint) {
      // Which deployment produced this, so a preview error is not read as a
      // production one, plus the commit and branch to look at.
      event.tags = {
        ...event.tags,
        deployment: environment,
        vercel_env: process.env.NEXT_PUBLIC_VERCEL_ENV ?? "local",
        commit:
          process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?.slice(0, 7) ?? "local",
        vercel_url: process.env.NEXT_PUBLIC_VERCEL_URL ?? "localhost",
        vercel_branch: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF ?? "local",
      };

      // Filter user-facing errors (validation, network, auth session errors).
      // Crash safeguard: unhandled/fatal events are always sent, even if their
      // message matches a noise pattern (see shouldSuppressEvent).
      const error = hint.originalException;
      const handled = event.exception?.values?.[0]?.mechanism?.handled;
      if (shouldSuppressEvent(event.level, handled, error)) {
        // Duck-type name/message so DOMException-shaped errors (AbortError via
        // fetch polyfills) keep their real name in the breadcrumb instead of
        // logging "Error: [object Object]".
        const { name: errorName, message: errorMessage } =
          getErrorNameAndMessage(error);

        // Add as breadcrumb for context, but don't send as error
        Sentry.addBreadcrumb({
          message: `[Filtered] ${errorName}: ${errorMessage}`,
          level: "warning",
          category: "filtered_error",
          data: { errorName },
        });
        return null; // Don't send to Sentry
      }

      if (
        dropNonErrorEventsInProduction &&
        environment === "production" &&
        !event.exception
      ) {
        return null;
      }

      // Remove user-related PII
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
      }

      // The URL an event carries is the one the page was on, which on a Stripe
      // return is still the dirty one. Headers alone were never enough.
      if (event.request?.url) {
        event.request.url = redactSensitiveUrl(event.request.url);
      }

      // Remove sensitive HTTP headers if present. One list, four call sites:
      // the two error hooks and the two transaction hooks. See ./redact-url.ts.
      if (event.request) deleteSensitiveHeaders(event.request);

      return event;
    },

    // Transactions never reach `beforeSend`, so the scrub above would miss a
    // sampled performance event carrying the same request line.
    beforeSendTransaction(event) {
      return redactTransactionEvent(event);
    },

    // Skip certain errors
    ignoreErrors: [
      "ECONNRESET",
      "ECONNREFUSED",
      "ETIMEDOUT",
      // Browser extension errors (not our code)
      /^Non-Error promise rejection captured/,
      // Chrome extensions
      /^chrome-extension/,
      /^moz-extension/,
      // A deploy replaced the chunk the open tab still asks for. Nothing to fix
      // in the code, and the next navigation resolves it.
      "ChunkLoadError",
      // A browser-engine warning, not an exception anyone can act on.
      "ResizeObserver loop limit exceeded",
      // Same category as the extension entries above: someone else's script
      // throwing inside our page. See ./ignore-errors.ts.
      ...META_IN_APP_BROWSER_ERRORS,
    ],

    // Limit breadcrumbs to prevent memory issues
    maxBreadcrumbs: 100,

    // Filter sensitive data from breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Filter out sensitive HTTP request data
      if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
        if (typeof breadcrumb.data?.url === "string") {
          breadcrumb.data.url = redactSensitiveUrl(breadcrumb.data.url);
        }
        // Remove request/response bodies that might contain sensitive data
        delete breadcrumb.data?.body;
        delete breadcrumb.data?.response_body;
      }

      // A navigation breadcrumb carries the address bar as it was BEFORE the
      // page could clean it: Sentry's history handler runs ahead of the
      // replaceState that removes a Stripe return. See ./redact-url.ts.
      if (breadcrumb.category === "navigation" && breadcrumb.data) {
        for (const key of ["from", "to"] as const) {
          const value: unknown = breadcrumb.data[key];
          if (typeof value === "string") {
            breadcrumb.data[key] = redactSensitiveUrl(value);
          }
        }
      }

      // Filter console breadcrumbs that might contain PII
      if (breadcrumb.category === "console" && breadcrumb.message) {
        const sensitivePatterns = [
          /email[:\s]*[\w.+-]+@[\w.-]+/gi,
          /phone[:\s]*[\d\s+-]+/gi,
          /password[:\s]*\S+/gi,
        ];
        for (const pattern of sensitivePatterns) {
          breadcrumb.message = breadcrumb.message.replace(
            pattern,
            "[REDACTED]",
          );
        }
      }

      return breadcrumb;
    },

    // Empty means the SDK's defaults and nothing more: `ConsentGatedReplay`
    // adds replay once the visitor agrees. See ./replay.ts.
    integrations: gateReplayOnConsent
      ? []
      : [Sentry.replayIntegration({ ...REPLAY_OPTIONS })],
  });
}
